// store/slices/realtimeSlice.ts
// Manages real-time state updates from SSE events
import { StateCreator } from "zustand";
import { JobStatus, JobType, EntityType, isTerminalStatus } from "@/services/jobsService";

export interface PlatformPublishStatus {
  status: "pending" | "publishing" | "published" | "failed";
  error?: string | null;
}

export interface PublishProgressState {
  postId: string;
  scheduledPostId?: string;
  status: "preparing" | "prepared" | "publishing" | "completed" | "error";
  platforms?: Record<string, PlatformPublishStatus>;
  error?: string;
  updatedAt: number;
}

// Job progress state from SSE events
// This handles all job types including image_analysis, text_generation, etc.
export interface JobProgressState {
  id: string;
  jobType: JobType;
  entityType: EntityType;
  entityId: string;
  status: JobStatus;
  progress: number;
  progressMessage: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  resultData?: Record<string, unknown> | null;
  updatedAt: number;
}

/** Standalone-ad creation progress, keyed by post_id.
 *
 * Backend emits `standalone_ad_progress` SSE events with `phase` set
 * to one of: queued | preparing | uploading_creative | creating_campaign
 * | completed | failed. The post card reads this to render a badge
 * while an ad is being created in the background. Cleared on
 * completed/failed after the toast fires. */
export interface StandaloneAdProgressState {
  postId: string;
  taskId: string;
  phase:
    | "queued"
    | "preparing"
    | "uploading_creative"
    | "creating_campaign"
    | "completed"
    | "failed";
  message?: string;
  error?: string;
  campaignId?: string | null;
  updatedAt: number;
}

export interface RealtimeState {
  publishProgress: Record<string, PublishProgressState>;
  /** Standalone-ad creation progress, keyed by post_id */
  standaloneAdProgress: Record<string, StandaloneAdProgressState>;
  // Unified job tracking via SSE - replaces domain-specific state
  jobs: Record<string, JobProgressState>;
}

export interface RealtimeSlice {
  realtime: RealtimeState;
  setPublishProgress: (postId: string, progress: Omit<PublishProgressState, "updatedAt">) => void;
  clearPublishProgress: (postId: string) => void;
  getPublishProgress: (postId: string) => PublishProgressState | null;
  setStandaloneAdProgress: (
    postId: string,
    progress: Omit<StandaloneAdProgressState, "updatedAt">,
  ) => void;
  clearStandaloneAdProgress: (postId: string) => void;
  getStandaloneAdProgress: (postId: string) => StandaloneAdProgressState | null;
  // Job tracking methods
  setJobProgress: (job: Omit<JobProgressState, "updatedAt">) => void;
  clearJob: (jobId: string) => void;
  getJob: (jobId: string) => JobProgressState | null;
  getJobForEntity: (entityType: EntityType, entityId: string) => JobProgressState | null;
  getActiveJobByType: (jobType: JobType) => JobProgressState | null;
  cleanupStaleJobs: (ttlMs?: number) => void;
}

export const createRealtimeSlice: StateCreator<RealtimeSlice> = (set, get) => ({
  realtime: {
    publishProgress: {},
    standaloneAdProgress: {},
    jobs: {},
  },

  setPublishProgress: (postId, progress) =>
    set((state) => ({
      realtime: {
        ...state.realtime,
        publishProgress: {
          ...state.realtime.publishProgress,
          [postId]: {
            ...progress,
            updatedAt: Date.now(),
          },
        },
      },
    })),

  clearPublishProgress: (postId) =>
    set((state) => {
      const { [postId]: _, ...rest } = state.realtime.publishProgress;
      return {
        realtime: {
          ...state.realtime,
          publishProgress: rest,
        },
      };
    }),

  getPublishProgress: (postId) => {
    return get().realtime.publishProgress[postId] || null;
  },

  setStandaloneAdProgress: (postId, progress) =>
    set((state) => ({
      realtime: {
        ...state.realtime,
        standaloneAdProgress: {
          ...state.realtime.standaloneAdProgress,
          [postId]: {
            ...progress,
            updatedAt: Date.now(),
          },
        },
      },
    })),

  clearStandaloneAdProgress: (postId) =>
    set((state) => {
      const { [postId]: _, ...rest } = state.realtime.standaloneAdProgress;
      return {
        realtime: {
          ...state.realtime,
          standaloneAdProgress: rest,
        },
      };
    }),

  getStandaloneAdProgress: (postId) => {
    return get().realtime.standaloneAdProgress[postId] || null;
  },

  // Job tracking methods
  setJobProgress: (job) =>
    set((state) => ({
      realtime: {
        ...state.realtime,
        jobs: {
          ...state.realtime.jobs,
          [job.id]: {
            ...job,
            updatedAt: Date.now(),
          },
        },
      },
    })),

  clearJob: (jobId) =>
    set((state) => {
      const { [jobId]: _, ...rest } = state.realtime.jobs;
      return {
        realtime: {
          ...state.realtime,
          jobs: rest,
        },
      };
    }),

  getJob: (jobId) => {
    return get().realtime.jobs[jobId] || null;
  },

  getJobForEntity: (entityType, entityId) => {
    const jobs = Object.values(get().realtime.jobs);
    // Find the most recent job for this entity
    const matchingJobs = jobs.filter(
      (j) => j.entityType === entityType && j.entityId === entityId
    );
    if (matchingJobs.length === 0) return null;
    // Return the most recently updated one
    return matchingJobs.reduce((latest, job) =>
      job.updatedAt > latest.updatedAt ? job : latest
    );
  },

  getActiveJobByType: (jobType) => {
    const jobs = Object.values(get().realtime.jobs);
    // Find the most recent active (non-terminal) job for this type
    const activeJobs = jobs.filter(
      (j) => j.jobType === jobType && !isTerminalStatus(j.status)
    );
    if (activeJobs.length === 0) return null;
    // Return the most recently updated one
    return activeJobs.reduce((latest, job) =>
      job.updatedAt > latest.updatedAt ? job : latest
    );
  },

  cleanupStaleJobs: (ttlMs = 5 * 60 * 1000) => {
    const now = Date.now();
    set((state) => {
      const jobs = { ...state.realtime.jobs };
      let cleaned = 0;

      for (const [jobId, job] of Object.entries(jobs)) {
        // Only remove jobs that are in terminal state AND older than TTL
        if (isTerminalStatus(job.status) && (now - job.updatedAt) > ttlMs) {
          delete jobs[jobId];
          cleaned++;
        }
      }

      if (cleaned > 0) {
        console.log(`🧹 Cleaned up ${cleaned} stale job(s)`);
      }

      return {
        realtime: {
          ...state.realtime,
          jobs,
        },
      };
    });
  },
});
